import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller } from "react-hook-form";
import {
  SIGN_CHOICES,
  PAIN_DESCRIPTION_OPTIONS,
  PAIN_WORSE_OPTIONS,
  PAIN_BETTER_OPTIONS,
  PAIN_CHRONIC_OPTIONS,
  initialFormData,
} from "./painScale.config";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import MultiSelect from "../../../../../FormFields/CustomSelect/MultiSelect";
import { useParams } from "react-router-dom";
import { createAbbeyPainScale } from "../../../../../../api/services/assessment";
import { QK } from "../../../../../../base/qk";
import { getCarePlans } from "../../../../../../api/services/core";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";

const CreateAbbeyPainScaleModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormData,
  });

  const createAssessmentMutation = useMutation(
    (assessmentData) => createAbbeyPainScale(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.abbeyPainScales, clientID]);
        enqueueSnackbar("Abbey Pain Scale assessment created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        client: clientID,
      };

      await createAssessmentMutation.mutateAsync(payload);
      handleCloseModal();
    } catch (error) {
      throw error;
    }
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Create Abbey Pain Scale Assessment
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Care Plan and Last Pain Relief */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      name="care_plan"
                      onChange={field.onChange}
                      value={field.value}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="last_pain_relief"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="last_pain_relief"
                      label="When was the last pain relief given?"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="e.g., Paracetamol 500mg at 9 AM"
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Pain Signs Section */}
            <Typography variant="h6" gutterBottom>
              Pain Signs Assessment
            </Typography>

            <Grid container spacing={2}>
              {/* Signs of Crying */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="signs_of_crying"
                  control={control}
                  rules={{ required: "Signs of crying assessment is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      name="signs_of_crying"
                      label="Are there any signs of whimpering/groaning/crying? *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SIGN_CHOICES}
                      error={!!errors.signs_of_crying}
                      helperText={errors.signs_of_crying?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sign_of_crying_details"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="sign_of_crying_details"
                      label="Crying Details"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>

              {/* Signs of Tension */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="signs_of_tension"
                  control={control}
                  rules={{
                    required: "Signs of tension assessment is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      name="signs_of_tension"
                      label="Are there any signs of tension/fright/frowning/grimacing? *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SIGN_CHOICES}
                      error={!!errors.signs_of_tension}
                      helperText={errors.signs_of_tension?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sign_of_tension_details"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="sign_of_tension_details"
                      label="Tension Details"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>

              {/* Signs of Fidgeting */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="signs_of_fidgeting"
                  control={control}
                  rules={{
                    required: "Signs of fidgeting assessment is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      name="signs_of_fidgeting"
                      label="Are there any signs of fidgeting/rocking/guarding part of their body/looking withdrawn? *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SIGN_CHOICES}
                      error={!!errors.signs_of_fidgeting}
                      helperText={errors.signs_of_fidgeting?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sign_of_fidgeting_details"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="sign_of_fidgeting_details"
                      label="Fidgeting Details"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>

              {/* Signs of Confusion */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="signs_of_confusion"
                  control={control}
                  rules={{
                    required: "Signs of confusion assessment is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      name="signs_of_confusion"
                      label="Are there any signs of increased confusion/refusal to eat/usual patterns altered? *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SIGN_CHOICES}
                      error={!!errors.signs_of_confusion}
                      helperText={errors.signs_of_confusion?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sign_of_confusion_details"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="sign_of_confusion_details"
                      label="Confusion Details"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>

              {/* Signs of Temperature */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="signs_of_temperature"
                  control={control}
                  rules={{
                    required: "Signs of temperature assessment is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      name="signs_of_temperature"
                      label="Are there any signs of change in temperature/pulse or blood pressure outside normal limits/perspiring/flushing/pallor? *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SIGN_CHOICES}
                      error={!!errors.signs_of_temperature}
                      helperText={errors.signs_of_temperature?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sign_of_temperature_details"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="sign_of_temperature_details"
                      label="Temperature Details"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>

              {/* Signs of Skin Tears */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="signs_of_skin_tears"
                  control={control}
                  rules={{
                    required: "Signs of skin tears assessment is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      name="signs_of_skin_tears"
                      label="Are there any signs of skin tears/pressure areas/arthritis/contractures/previous injuries? *"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={SIGN_CHOICES}
                      error={!!errors.signs_of_skin_tears}
                      helperText={errors.signs_of_skin_tears?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sign_of_skin_tears_details"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="sign_of_skin_tears_details"
                      label="Skin Tears Details"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Pain Details Section */}
            <Typography variant="h6" gutterBottom>
              Pain Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="place_of_pain"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="place_of_pain"
                      label="If known, where is the pain?"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                      placeholder="e.g., Lower back and right shoulder"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="cause_of_pain"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="cause_of_pain"
                      label="If known, what is the cause of pain?"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={2}
                      placeholder="e.g., Post-fall trauma"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="pain_present_for"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="pain_present_for"
                      label="If known, how long has the pain been present?"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="e.g., 3 days"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="pain_chronic"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      name="pain_chronic"
                      label="If known, is the pain chronic/acute/acute on chronic?"
                      selectedValue={field.value}
                      onChange={field.onChange}
                      options={PAIN_CHRONIC_OPTIONS}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Pain Characteristics Section */}
            <Typography variant="h6" gutterBottom>
              Pain Characteristics
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Description of Pain
                </Typography>
                <Controller
                  name="description_of_pain"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      options={PAIN_DESCRIPTION_OPTIONS}
                      value={PAIN_DESCRIPTION_OPTIONS.filter((option) =>
                        field.value?.includes(option.value)
                      )}
                      onChange={(selected) =>
                        field.onChange(
                          selected ? selected.map((option) => option.value) : []
                        )
                      }
                      placeholder="If able to say, what is the service user description of pain"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  What Makes Pain Worse
                </Typography>
                <Controller
                  name="make_pain_worse"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      options={PAIN_WORSE_OPTIONS}
                      value={PAIN_WORSE_OPTIONS.filter((option) =>
                        field.value?.includes(option.value)
                      )}
                      onChange={(selected) =>
                        field.onChange(
                          selected ? selected.map((option) => option.value) : []
                        )
                      }
                      placeholder="If able to say, what makes the pain worse?"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  What Makes Pain Better
                </Typography>
                <Controller
                  name="make_pain_better"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      options={PAIN_BETTER_OPTIONS}
                      value={PAIN_BETTER_OPTIONS.filter((option) =>
                        field.value?.includes(option.value)
                      )}
                      onChange={(selected) =>
                        field.onChange(
                          selected ? selected.map((option) => option.value) : []
                        )
                      }
                      placeholder="If able to say, what makes the pain better?"
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Comments */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="comments"
                      label="Comments"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={4}
                      placeholder="Comments including action required (please include in 'support to be delivered' as necessary)"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
        >
          Create Assessment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAbbeyPainScaleModal;
